import { bootScriptJs, bootScriptTag, isInappEnv, isPbsEnabled, Site, SiteEnv, SiteProduct, siteProductOf } from '@pubstack/common/src/site'

/**
 * One connection step: a thing the publisher does to their pages, usually a snippet to copy.
 *
 * Deliberately an id rather than a rendered block. The question "what does this media need?" is asked on
 * five screens — the media flyout, both media detail pages, the settings flyout and the modal that opens
 * the moment a media is created — and each dresses the answer differently. Answering it in one place and
 * dressing it in another is what keeps the five from drifting again.
 */
export const CONNECT_STEPS = ['html-tag', 'javascript-tag', 'gtm', 'pubstack-adapter', 'sdk-init', 'amp-code', 'amp-cookie-sync'] as const
export type ConnectStep = (typeof CONNECT_STEPS)[number]

/** A piece of guidance around the snippets, rather than a snippet. */
export const CONNECT_NOTES = ['pubstack-adapter', 'prebid-sdk', 'site-preparation'] as const
export type ConnectNote = (typeof CONNECT_NOTES)[number]

export type ConnectPlan = {
  /** What the media runs on. The lead-in and the whole shape of the plan hang off it. */
  product: SiteProduct
  env: SiteEnv
  steps: ConnectStep[]
  notes: ConnectNote[]
  /**
   * The one line saying what this media is being connected *for*, or nothing where no tag is shown.
   *
   * Absent off the web on purpose: it speaks of a tag in the page, and an app has no page to put one in.
   */
  lead?: string
  /**
   * Node is picked but switched off, so there is nothing to connect to yet.
   *
   * Not simply "no steps": a media with nothing to show because its product is not activated needs to be
   * sent to the activation page, where one with nothing to show because the scope has no Analytics has
   * nowhere to be sent.
   */
  pendingActivation: boolean
}

export type ConnectAudience = {
  /**
   * Whether the **scope** was sold Analytics — `scope.hasFeature('analytics')`, never the viewer's own.
   *
   * These screens describe the account rather than gate the viewer: a Pubstack user gets every feature
   * synthesised for whichever scope they are in, so asking the user would show staff a tag the customer
   * does not have, which is the opposite of what support needs to see.
   */
  hasAnalytics: boolean
  /**
   * Ask for the plain Analytics tag whatever the media runs on.
   *
   * The settings media flyout is the one place that offers a One media the measurement tag without the ad
   * management runtime — One's own page links to it with "keep your monetisation settings on a page". Left
   * to the media's product it would serve the `/adm` tag there, which is the very thing that reader came to
   * avoid.
   */
  analyticsOnly?: boolean
}

/**
 * The boot tag is the Analytics tag, so a scope that was never sold Analytics is not offered it — except
 * on One, where the very same tag carries the ad management runtime and withholding it would leave a
 * paying customer unable to connect the media they just bought.
 */
const showsBootScript = (product: SiteProduct, { hasAnalytics }: ConnectAudience): boolean => product === 'adm' || hasAnalytics

/**
 * Whether the only honest answer is "activate the product first".
 *
 * Asked of the flag rather than of `siteProductOf`, which reads a switched-off Node media as Analytics:
 * such a media sits on Node's pages, and what it needs is the activation it never had — not the Analytics
 * tag of a product nobody put it on. One is excluded because a legacy media can carry One's flag and Node's
 * `inactive` at once, and One's tag is perfectly ready to be pasted.
 *
 * Off the web there is no second product to fall back on, so an unactivated media has nothing at all.
 */
const isPendingActivation = (site: Site, product: SiteProduct): boolean => (site.env === 'web' ? site.pbsStandaloneStatus === 'inactive' && product !== 'adm' : !isPbsEnabled(site))

/**
 * What this media is being connected *for*.
 *
 * The three products connect through tags that look alike and do different things, and the screens used to
 * show the snippet with no word on which runtime it boots. That is how a publisher ends up with One's tag
 * and Analytics' tag on the same page, each reporting the same auctions.
 */
/** Asked for by name, so it describes the tag rather than the media — which may well be running One. */
const ANALYTICS_ONLY_LEAD = 'This tag reports the auctions running in the page and deploys nothing. Use it where the media’s monetisation is managed on the page itself.'

const CONNECT_PRODUCT_LEAD: Record<SiteProduct, string> = {
  adm: 'This tag carries both ad management and analytics. It is the only Pubstack tag this media needs.',
  pbs: 'This tag reports the auctions running in the page. The server-side auctions are connected separately, below.',
  analytics: 'This tag reports the auctions running in the page. Nothing is deployed on this media.',
}

/**
 * Everything a media needs to be connected, in the order it is shown.
 *
 * Read off the media's product and environment rather than the scope's: a scope can own both products,
 * and what the publisher has to paste is decided by the one media in front of them. The scope only ever
 * narrows — see `showsBootScript`.
 */
export const connectPlanOf = (site: Site, audience: ConnectAudience): ConnectPlan => {
  // Not `siteProductOf`, which answers `adm` for a media carrying both flags: the two products are asked
  // separately here because a media can need One's tag *and* Node's server-side config at once. A media
  // predating the one-product rule does, and gating the config on the tie-break left it with no way to
  // configure the auctions it was already running.
  const product = audience.analyticsOnly ? 'analytics' : siteProductOf(site)
  const runsNode = !audience.analyticsOnly && isPbsEnabled(site)
  const steps: ConnectStep[] = []
  const notes: ConnectNote[] = []

  if (site.env === 'web') {
    if (showsBootScript(product, audience)) {
      steps.push('html-tag', 'javascript-tag', 'gtm')
    }
    if (product === 'adm') {
      notes.push('site-preparation')
    }
    // Node's auctions go through the Pubstack bid adapter, which the boot tag does not carry.
    if (runsNode) {
      steps.push('pubstack-adapter')
      notes.push('pubstack-adapter')
    }
  }

  // Off the web there is only ever Node, and nothing to hand over until it is switched on: the snippet
  // names a Prebid Server account, and one that was never activated takes no bids.
  if (runsNode && isInappEnv(site.env)) {
    steps.push('sdk-init')
    notes.push('prebid-sdk')
  }

  if (runsNode && site.env === 'amp') {
    steps.push('amp-code', 'amp-cookie-sync')
  }

  return {
    product,
    env: site.env,
    steps,
    notes,
    lead: !steps.includes('html-tag') ? undefined : audience.analyticsOnly ? ANALYTICS_ONLY_LEAD : CONNECT_PRODUCT_LEAD[product],
    pendingActivation: !audience.analyticsOnly && isPendingActivation(site, product),
  }
}

/** The code each step offers, or `undefined` for the steps that are prose. */
export const connectSnippetOf = (step: ConnectStep, site: Site, product: SiteProduct): string | undefined => {
  switch (step) {
    case 'html-tag':
      return bootScriptTag(site.id, product)
    case 'javascript-tag':
      return bootScriptJs(site.id, product)
    case 'gtm':
      return undefined
    case 'pubstack-adapter':
      return pubstackAdapterConfig(site.id)
    case 'sdk-init':
      return site.env === 'ios' ? iosConfiguration(site.id) : androidConfiguration(site.id)
    case 'amp-code':
      return AMP_CONFIGURATION
    case 'amp-cookie-sync':
      return ampCookieSync(site.id)
  }
}

/** What Prebid.js needs to bid through the Pubstack adapter: the iframe user sync, and the bid on each ad unit. */
const pubstackAdapterConfig = (siteId: string): string =>
  `pbjs.setConfig({\n` +
  `\tuserSync: {\n` +
  `\t\tiframeEnabled: true,\n` +
  `\t\tfilterSettings: { iframe: { bidders: ['pubstack'], filter: 'include' } }\n` +
  `\t},\n` +
  `\t// Optional, if you use tcfControl\n` +
  `\tgvlMapping: { pubstack: 1408 }\n` +
  `})\n\n` +
  `// On each ad unit\n` +
  `{\n` +
  `\tcode: '<ad unit code>',\n` +
  `\tbids: [{\n` +
  `\t\tbidder: 'pubstack',\n` +
  `\t\tparams: { siteId: '${siteId}', adUnitName: '<ad unit name in Pubstack>' }\n` +
  `\t}]\n` +
  `}\n`

const iosConfiguration = (siteId: string): string => `import PrebidMobile

// Prebid configuration for iOS
Prebid.shared.prebidServerAccountId = "${siteId}"
Prebid.shared.prebidServerHost = Host.Custom
Prebid.shared.customPrebidServerUrl = "https://prebid-server.pbstck.com/openrtb2/auction"

PrebidMobile.initializeSDK { status in
    switch status {
    case .succeeded:
        print("Prebid SDK initialized ✅")
    case .failed:
        print("Prebid SDK init failed ❌")
    @unknown default:
        print("Prebid SDK init status unknown ⚠️")
    }
}
`

const androidConfiguration = (siteId: string): string => `import org.prebid.mobile.PrebidMobile

// Kotlin (Android) – Prebid Configuration
PrebidMobile.setPrebidServerAccountId("${siteId}")
PrebidMobile.initializeSdk(
    context,
    "https://prebid-server.pbstck.com/openrtb2/auction",
) { status ->
    if (status == PrebidMobile.InitializationStatus.Succeeded) {
        Log.d("Prebid", "SDK initialized ✅")
    } else {
        Log.e("Prebid", "SDK init failed: $status")
    }
}`

/** The `amp-ad` element AMP pages load before any Pubstack ad renders. */
export const AMP_CONFIGURATION = `<script
    async
    custom-element="amp-ad"
    src="https://cdn.ampproject.org/v0/amp-ad-0.1.js"
></script>`

const ampCookieSync = (siteId: string): string => `<amp-iframe
  width="1"
  height="1"
  title="User Sync"
  sandbox="allow-scripts allow-same-origin"
  frameborder="0"
  src="https://acdn.adnxs.com/prebid/amp/user-sync/load-cookie-with-consent.html?endpoint=https%3A%2F%2Fprebid-server.pbstck.com%2Fcookie_sync&max_sync_count=8&source=amp&bidders=pubstack&args=account:${siteId}">
  <amp-img
    layout="fill"
    src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
    placeholder
  ></amp-img>
</amp-iframe>`
